<template>
  <div>
    <FormError v-if="formError.length > 0" :errorMessages="formError" />

    <CardBase :headerBorder="false">
      <template #header>{{ $route.meta.title }}</template>
      <template #content>
        <form
          class="form form-search"
          method="get"
          @submit.prevent="reloadTable"
        >
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Search</label>
              <input
                type="text"
                class="form__input"
                placeholder="Ticket / Username / Member Bank Account"
                v-model="formData.search"
                ref="searchform"
              />
            </div>
            <div class="form-search__double">
              <label class="form__label">Date Order</label>
              <select class="form__input" v-model="tableData.sort_dir">
                <option value="asc">Oldest First</option>
                <option value="desc">Newest First</option>
              </select>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Group</label>
              <select
                class="form__input"
                v-model="formData.bank_group_id"
                @change="filterListBank"
              >
                <option value="">Show All</option>
                <option
                  v-for="row in listBankGroup"
                  :key="row.id"
                  :value="row.id"
                >
                  {{ row.name }}
                </option>
              </select>
            </div>
            <div class="form-search__double">
              <label class="form__label">Bank</label>
              <select class="form__input" v-model="formData.bank_id">
                <option value="">Show All</option>
                <option v-for="row in listBank" :key="row.id" :value="row.id">
                  {{ row.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Start Date</label>
              <date-picker
                v-model="formData.start_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="Start Date"
                @close="checkEndDate"
                :disabled-date="notAfterToday"
              ></date-picker>
            </div>
            <div class="form-search__double">
              <label class="form__label">End Date</label>
              <date-picker
                v-model="formData.end_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="End Date"
                :disabled-date="notBeforeStart"
              ></date-picker>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Asset</label>
              <multiselect
                v-model="formData.asset"
                :options="listAsset"
                :loading="listAssetLoading"
                :close-on-select="true"
                :hide-selected="false"
                track-by="id"
                label="name"
                :placeholder="
                  formData.asset ? formData.asset.name : 'All Asset'
                "
                :deselectLabel="'Remove selection'"
                :selectLabel="'Select this option'"
                @input="
                  formData.asset_id = formData.asset ? formData.asset.id : ''
                "
              >
                <template #noResult>No results found.</template>
              </multiselect>
            </div>
            <div class="form-search__double">
              <label class="form__label">Status</label>
              <select class="form__input" v-model="formData.status">
                <option value="3">Show All</option>
                <option value="-1">Missing Data / Action Required</option>
                <option value="1">Processed</option>
                <option value="2">Hidden</option>
              </select>
            </div>
          </div>

          <div class="form-search__single">
            <FormNote class="mb-6"
              >Note: Start Date & End Date tidak akan berlaku jika melakukan
              search dengan status Missing Data.</FormNote
            >
          </div>

          <div class="form-search__action">
            <div class="w-3/12 flex">
              <BaseButton type="submit" class="mr-2" buttonClass="secondary"
                >Search</BaseButton
              >
              <BaseButton type="button" buttonClass="default" @click="reset"
                >Reset Search</BaseButton
              >
            </div>
          </div>
        </form>

        <FormNote class="mb-6"
          >Note:
          <strong
            >Transaksi yang bisa di <em>hide</em> hanya transaksi nomor rekening
            bank yang valid.</strong
          >
          Jika transaksi sudah di <em>hide</em>,
          <strong>tidak bisa di <em>show</em> lagi.</strong> Deposit harus
          ditambahkan secara manual.</FormNote
        >

        <vue-good-table
          :key="tableKey"
          mode="remote"
          theme="polar-bear"
          :rows="tableData.rows"
          :columns="tableData.columns"
          :total-rows="tableData.totalRows"
          :isLoading="tableData.isLoading"
          :sort-options="{
            enabled: false,
          }"
          :pagination-options="{
            enabled: true,
            perPageDropdown: perPageDropdown,
            dropdownAllowAll: false,
            setCurrentPage: tableData.currentPage,
            infoFn: tablePageInfo,
          }"
          styleClass="vgt-table striped bordered"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'username'">
              <font-awesome-icon
                v-if="!props.row.member_account"
                :icon="['fas', 'exclamation-triangle']"
                class="mr-1 text-red-500"
              />
              {{ props.row.username }}
            </span>
            <span v-else-if="props.column.field == 'amount_total'">
              {{ props.row.amount_total }}
              <span
                class="block font-semibold text-pink-700"
                v-if="
                  props.row.amount_fee &&
                    props.row.amount_fee !== '0.00' &&
                    props.row.amount_fee !== '-987,123,000.00'
                "
                >Fee: {{ props.row.amount_fee }}</span
              >
            </span>
            <span v-else-if="props.column.field == 'member_bank_account_id'">
              <font-awesome-icon
                v-if="!props.row.member_bank_account"
                :icon="['fas', 'exclamation-triangle']"
                class="mr-1 text-red-500"
              />
              <template v-if="props.row.member_bank_account_number">
                [{{ props.row.member_bank_name }}]
                {{ props.row.member_bank_account_holder }} -
                {{ props.row.member_bank_account_number }}
              </template>
              <template v-else>-</template>

              <multiselect
                ref="multiselect"
                class="mt-2"
                v-if="
                  props.row.status == -1 &&
                    props.row.member_account &&
                    !props.row.member_bank_account
                "
                v-model="fixBank"
                :options="listMemberBank"
                :loading="listMemberBankLoading"
                :close-on-select="true"
                :hide-selected="false"
                track-by="id"
                :searchable="false"
                label="tooltip"
                placeholder="Choose member bank"
                :deselectLabel="''"
                :selectLabel="''"
                @open="getListMemberBank(props.row.id)"
                @input="
                  updateMemberBank(
                    $event,
                    '[' +
                      props.row.asset_name +
                      ' - ' +
                      props.row.ref +
                      '] ' +
                      props.row.username,
                    props.row.id
                  )
                "
              >
                <template #noOptions>
                  Choose bank for {{ props.row.username }}</template
                >
                <template slot="option" slot-scope="props">
                  <div class="block leading-5">
                    [{{ props.option.bank.name }}] {{ props.option.holder }} -
                    {{ props.option.number }}
                  </div>
                </template>
              </multiselect>
            </span>
            <span v-else-if="props.column.field == 'bank_account_id'">
              <font-awesome-icon
                v-if="!props.row.bank_account"
                :icon="['fas', 'exclamation-triangle']"
                class="mr-1 text-red-500"
              />
              [{{ props.row.bank_name }}] {{ props.row.bank_account_holder }} -
              {{ props.row.bank_account_number }}
            </span>
            <span v-else-if="props.column.field == 'status'">
              <span
                class="flex items-center text-yellow-500"
                v-if="props.row.status == 2"
              >
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="mr-2"
                  size="lg"
                />Hidden
              </span>

              <span class="block mt-1" v-if="props.row.status == 2">
                {{ props.row.updated_by }} / {{ props.row.updated_at }}
              </span>

              <span
                class="flex items-center text-green-500"
                v-if="props.row.status == 1"
              >
                <font-awesome-icon
                  :icon="['fas', 'check-circle']"
                  class="mr-2"
                  size="lg"
                />Approved
              </span>

              <span
                class="flex items-center text-red-500"
                v-else-if="props.row.status == -1"
              >
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="mr-2"
                  size="lg"
                />Missing Data
              </span>

              <span
                class="flex items-center text-blue-500"
                v-else-if="props.row.status == -2"
              >
                <font-awesome-icon
                  :icon="['fas', 'edit']"
                  class="mr-2"
                  size="lg"
                />Action Required
              </span>
            </span>
            <span v-else-if="props.column.field == 'uuid'">
              <span v-if="props.row.status == -2">
                <input
                  type="text"
                  class="form__input"
                  placeholder="Withdraw Fee"
                  :ref="'fee-' + props.row.id"
                  @blur="checkFee(props.row.id)"
                  v-currency="{
                    currency: null,
                    distractionFree: false,
                    allowNegative: false,
                    locale: 'en',
                  }"
                />
                <button
                  type="button"
                  class="
                    mt-3
                  w-full
                  border-blue-600
                  button
                  border border-solid
                  hover:bg-blue-600
                  hover:text-white
                  transition-all
                  duration-200
                  text-center text-blue-600
                "
                  @click="approveWithdraw(props.row.id)"
                >
                  Approve
                </button>
              </span>
              <router-link
                v-if="
                  userCan('update.withdraw') &&
                    props.row.status == 1 &&
                    props.row.member_transaction_id
                "
                :to="{
                  name: 'WithdrawEdit',
                  params: { id: props.row.member_transaction_id },
                }"
                class="
                  w-full
                  button
                  border-gray-600
                  button
                  border border-solid
                  hover:bg-gray-600
                  hover:text-white
                  transition-all
                  duration-200
                  text-center text-gray-600
                "
                >Edit</router-link
              >
              <button
                v-else-if="props.row.hide"
                type="button"
                class="
                  w-full
                  border-yellow-600
                  button
                  border border-solid
                  hover:bg-yellow-600
                  hover:text-white
                  transition-all
                  duration-200
                  text-center text-yellow-600
                "
                @click="toggleTransaction(props.row.id, 2)"
              >
                <font-awesome-icon :icon="['far', 'eye-slash']" />
                Hide
              </button>
            </span>
          </template>
        </vue-good-table>
      </template>
    </CardBase>
  </div>
</template>
<script>
import { CurrencyDirective, parse } from "vue-currency-input";
import { showListMixin } from "@/mixins/showListMixin";
import { datePickerMixin } from "@/mixins/datePickerMixin";
import DatePicker from "vue2-datepicker";
import { getListBankOptionsMixin } from "@/mixins/getListBankOptionsMixin";

export default {
  directives: {
    currency: CurrencyDirective,
  },
  data() {
    return {
      formError: [],
      fixBank: null,
      formData: {
        status: -1,
        asset: null,
        asset_id: "",
        bank_group_id: "",
        bank_id: "",
        search: "",
        start_date: null,
        end_date: null,
      },
      listMemberBank: [],
      listMemberBankLoading: false,
      listAsset: [],
      listAssetLoading: true,

      tableData: {
        columns: [
          {
            label: "Date",
            field: "date",
          },
          {
            label: "Asset",
            field: "asset_name",
          },
          {
            label: "Ticket #",
            field: "ref",
          },
          {
            label: "Username",
            field: "username",
          },
          {
            label: "Member Bank",
            field: "member_bank_account_id",
            width: "40%",
          },
          {
            label: "Bank Account",
            field: "bank_account_id",
            width: "40%",
          },
          {
            label: "Amount",
            field: "amount_total",
          },
          {
            label: "Status",
            field: "status",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
            hidden: false,
            width: "40%",
          },
        ],
        sort_by: "date",
        sort_dir: "desc",
      },
    };
  },
  mixins: [showListMixin, datePickerMixin, getListBankOptionsMixin],
  components: {
    DatePicker,
  },
  created() {
    this.getListAsset();
    this.getListBank();
    this.getListBankGroup();
  },
  watch: {
    $route() {
      const queryParams = this.$route.query;

      if (queryParams.asset_id != undefined) {
        this.listAsset.forEach((data) => {
          if (data.id == queryParams.asset_id) {
            this.formData.asset = data;
            this.formData.asset_id = data.id;
          }
        });
      }
    },
  },
  methods: {
    filterListBank() {
      this.formData.bank_id = "";
      this.getListBank(this.formData.bank_group_id);
    },
    approveWithdraw(id) {
      const url =
        process.env.VUE_APP_API_URL + "/nexus-transaction/" + id + "/fee";

      this.tableData.isLoading = true;

      this.$http({
        method: "patch",
        url: url,
        data: {
          amount: this.$refs["fee-" + id].value,
        },
      })
        .then((res) => {
          if (res.status == 200 || res.status == 201) {
            this.$swal({
              position: "bottom-end",
              showConfirmButton: false,
              toast: true,
              timer: 5000,
              timerProgressBar: true,
              title: res.data.message,
              icon: "success",
            });

            this.loadTableData("wew");
          }
        })
        .catch((error) => {
          if (error.response) {
            this.formError = [];

            if (error.response.status == 409) {
              this.formError[0] = "<pre>" + error.response.data + "</pre>";
            } else {
              for (var err in error.response.data) {
                for (var msg in error.response.data[err]) {
                  this.formError.push(error.response.data[err][msg]);
                }
              }
            }
          }
        })
        .finally(() => {});
    },
    parseCurrency(data) {
      return parse(data);
    },
    checkFee(id) {
      const feeInput = this.$refs["fee-" + id];
      if (!feeInput.value) feeInput.value = 0;
    },
    isEmoneyNexusPay(data) {
      const { bank_account_holder, bank_account_number } = data;

      if (
        bank_account_holder === "EMONEY NEXUSPAY" &&
        bank_account_number == "0000000000"
      )
        return true;
      else return false;
    },
    updateMemberBank(option, title, depositId) {
      const memberBankId = option.id;

      this.$refs.searchform.focus();

      this.$swal({
        title: title,
        html: `Sync Transaction To:<br>[${option.bank.name}] ${option.holder} ${option.number}`,
        icon: "info",
        showCancelButton: true,
        reverseButtons: true,
      })
        .then((res) => {
          if (res.value) {
            this.$http
              .patch(
                process.env.VUE_APP_API_URL +
                  "/nexus-transaction/" +
                  depositId +
                  "/bank-account",
                {
                  member_bank_account_id: memberBankId,
                }
              )
              .then((res) => {
                this.$swal({
                  position: "bottom-end",
                  showConfirmButton: false,
                  toast: true,
                  timer: 5000,
                  timerProgressBar: true,
                  title: res.data.message,
                  icon: "success",
                });

                this.loadTableData();
              });
          }
        })
        .finally(() => {
          this.fixBank = null;
          this.listMemberBank = [];
        });
    },
    getListMemberBank(id) {
      this.listMemberBankLoading = true;
      this.listMemberBank = [];

      this.$http
        .get(process.env.VUE_APP_API_URL + "/nexus-transaction/" + id)
        .then((res) => {
          this.listMemberBank = res.data.data;
        })
        .finally(() => {
          this.listMemberBankLoading = false;
        });
    },
    parseBankNumber(bankAccount) {
      bankAccount = parseInt(bankAccount);
      if (Number.isInteger(bankAccount)) {
        return false;
      } else {
        return true;
      }
    },
    reset() {
      this.formData.search = "";
      this.formData.start_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.end_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.status = "-1";
      this.formData.asset = null;
      this.formData.asset_id = "";
      this.tableData.sort_dir = "desc";
      this.tableData.limit = 10;

      if (this.$route.query.search == undefined) {
        this.tableKey++;
      } else {
        this.tableData.currentPage = 1;
        this.$router.push({ name: "NexusWithdrawList" }).catch(() => {});
      }
    },
    reloadTable() {
      if (this.formData.search && this.formData.search.length < 3) {
        this.$swal({
          position: "bottom-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
          title: "Search query must have at least 3 characters",
          icon: "warning",
        });
      } else {
        this.tableData.currentPage = 1;
        this.$router
          .replace({
            query: {
              search: this.formData.search,
              start_date: this.formData.start_date,
              end_date: this.formData.end_date,
              bank_group_id: this.formData.bank_group_id,
              bank_id: this.formData.bank_id,
              asset_id: this.formData.asset_id,
              status: this.formData.status,
              sort_dir: this.tableData.sort_dir,
              tz: new Date().getTime(),
            },
          })
          .catch(() => {});
      }
    },
    loadTableData(a = "") {
      console.log(a);
      this.tableData.isLoading = true;

      const queryParams = this.$route.query;
      let searchParam = "";

      if (queryParams.search != undefined) {
        this.formData = { ...this.formData, ...this.$route.query };
        this.tableData.sort_dir = this.$route.query.sort_dir;
        for (const key in queryParams) {
          if (key != "sort_dir") {
            searchParam += `&${key}=${queryParams[key]}`;
          }
        }

        if (queryParams.status == 2) {
          this.tableData.columns[8].hidden = true;
        } else {
          this.tableData.columns[8].hidden = false;
        }
      } else {
        searchParam += `&start_date=${this.formData.start_date}&end_date=${this.formData.end_date}&status=-1`;
      }

      this.$http
        .get(
          process.env.VUE_APP_API_URL +
            `/nexus-transaction?type_id=2&page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
        )
        .then((res) => {
          this.tableData.rows = res.data.data;
          this.tableData.totalRows = res.data.total;
          this.exportToken = res.data.export_token;
          this.$emit("getBankData");
        })
        .finally(() => {
          this.tableData.isLoading = false;
        });
    },
    getListAsset() {
      this.$http
        .get(process.env.VUE_APP_API_URL + "/asset/sync")
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listAsset = response.data;
              const queryParams = this.$route.query;

              if (queryParams.asset_id != undefined) {
                this.listAsset.forEach((data) => {
                  if (data.id == queryParams.asset_id) {
                    this.formData.asset = data;
                  }
                });
              }
            }
          }
        })
        .finally(() => {
          this.listAssetLoading = false;
        });
    },
    toggleTransaction(id, status) {
      const url = process.env.VUE_APP_API_URL + "/nexus-transaction/" + id;

      this.tableData.isLoading = true;

      this.$http({
        method: "patch",
        url: url,
        data: {
          status: status,
        },
      })
        .then((res) => {
          if (res.status == 200 || res.status == 201) {
            this.$swal({
              position: "bottom-end",
              showConfirmButton: false,
              toast: true,
              timer: 5000,
              timerProgressBar: true,
              title: res.data.message,
              icon: "success",
            });

            this.loadTableData();
          }
        })
        .catch((error) => {
          if (error.response) {
            this.formError = [];

            if (error.response.status == 409) {
              this.formError[0] = "<pre>" + error.response.data + "</pre>";
            } else {
              for (var err in error.response.data) {
                for (var msg in error.response.data[err]) {
                  this.formError.push(error.response.data[err][msg]);
                }
              }
            }
          }
        })
        .finally(() => {});
    },
  },
};
</script>
